import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { FaCheckCircle, FaMoneyBillWave, FaHandshake, FaFileSignature, FaTimes, FaChevronLeft, FaChevronRight, FaExpand, FaImage } from 'react-icons/fa';
import { useFullscreen } from '../../contexts/FullscreenContext';

const ProjectMain = ({ project, t }) => {
  const [showVideo, setShowVideo] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // index utilisé pour l'image principale et le plein écran
  const thumbnailsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { enableFullscreen, disableFullscreen } = useFullscreen();
  // Créer un tableau de galerie à partir des images disponibles
  const gallery = useMemo(() => {
    if (project.gallery && project.gallery.length > 0) {
      return project.gallery;
    }
    return project.image ? [project.image] : [];
  }, [project.gallery, project.image]);

  // Met à jour l'état de scroll possible sur les miniatures
  const updateScrollButtons = useCallback(() => {
    const el = thumbnailsRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1); // petit offset pour éviter les effets de bord
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const handleResize = () => updateScrollButtons();
    window.addEventListener('resize', handleResize);

    const el = thumbnailsRef.current;
    if (el) {
      el.addEventListener('scroll', updateScrollButtons);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (el) {
        el.removeEventListener('scroll', updateScrollButtons);
      }
    };
  }, [gallery, updateScrollButtons]);

  // Gestionnaire pour ouvrir une image en plein écran
  const openFullscreenImage = (index) => {
    setCurrentImageIndex(index);
    setFullscreenImage(gallery[index]);
    document.body.style.overflow = 'hidden'; // Empêcher le défilement de la page
    enableFullscreen(); // Activer le mode plein écran pour masquer le bouton flottant
  };

  // Gestionnaire pour fermer le mode plein écran
  const closeFullscreenImage = useCallback(() => {
    setFullscreenImage(null);
    document.body.style.overflow = 'auto'; // Rétablir le défilement de la page
    disableFullscreen(); // Désactiver le mode plein écran pour réafficher le bouton flottant
  }, [disableFullscreen]);

  // Gestionnaire pour la navigation entre les images
  const navigateImage = useCallback((direction) => {
    setCurrentImageIndex(prevIndex => {
      let newIndex = prevIndex + direction;
      if (newIndex < 0) newIndex = gallery.length - 1;
      if (newIndex >= gallery.length) newIndex = 0;
      setFullscreenImage(gallery[newIndex]);
      return newIndex;
    });
  }, [gallery]);

  // Gestionnaire pour les touches du clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!fullscreenImage) return;
      
      if (e.key === 'Escape') {
        closeFullscreenImage();
      } else if (e.key === 'ArrowLeft') {
        navigateImage(-1);
      } else if (e.key === 'ArrowRight') {
        navigateImage(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [fullscreenImage, navigateImage, closeFullscreenImage]);

  // Afficher un message si aucune image n'est disponible
  if (gallery.length === 0) {
    return (
      <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 mb-8">
        <div className="text-center">
          <FaImage className="mx-auto text-4xl mb-2" />
          <p>{t('realEstateDetail.gallery.noImages')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:col-span-2">
      {/* Galerie */}
      <div className="mb-8">
        <div className="relative">
          {/* Image principale avec bouton plein écran (ratio fixe pour mobile) */}
          <div className="relative w-full rounded-lg overflow-hidden bg-gray-100 group pb-[66%] sm:pb-[56%]">
            <img 
              src={gallery[currentImageIndex]} 
              alt={project.title} 
              className="absolute inset-0 w-full h-full object-cover cursor-pointer"
              onClick={() => openFullscreenImage(currentImageIndex)}
            />
            <button 
              onClick={(e) => {
                e.stopPropagation();
                openFullscreenImage(currentImageIndex);
              }}
              className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              aria-label="Voir en plein écran"
            >
              <FaExpand />
            </button>
          </div>
          
          {/* Miniatures : toutes les images du projet, avec défilement horizontal */}
          {gallery.length > 0 && (
            <div className="relative mt-2">
              {/* Bouton défilement gauche */}
              <button
                type="button"
                onClick={() => {
                  if (thumbnailsRef.current) {
                    thumbnailsRef.current.scrollBy({ left: -150, behavior: 'smooth' });
                  }
                }}
                className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 shadow rounded-full w-8 h-8 items-center justify-center ${canScrollLeft ? 'flex' : 'hidden'}`}
                aria-label="Faire défiler les images vers la gauche"
              >
                <FaChevronLeft size={14} />
              </button>

              {/* Liste des miniatures */}
              <div
                ref={thumbnailsRef}
                className="flex gap-2 overflow-x-auto pb-1 px-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
              >
                {gallery.map((img, index) => (
                  <div
                    key={index}
                    className={`relative h-20 w-28 flex-shrink-0 rounded overflow-hidden group border ${index === currentImageIndex ? 'border-socabeg-blue' : 'border-transparent'}`}
                    onMouseEnter={() => setCurrentImageIndex(index)}
                  >
                    <img
                      src={img}
                      alt={`${project.title} ${index + 1}`}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => openFullscreenImage(index)}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openFullscreenImage(index);
                      }}
                      className="absolute inset-0 m-auto w-8 h-8 bg-black bg-opacity-50 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center"
                      aria-label="Voir en plein écran"
                    >
                      <FaExpand size={12} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Bouton défilement droite */}
              <button
                type="button"
                onClick={() => {
                  if (thumbnailsRef.current) {
                    thumbnailsRef.current.scrollBy({ left: 150, behavior: 'smooth' });
                  }
                }}
                className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 shadow rounded-full w-8 h-8 items-center justify-center ${canScrollRight ? 'flex' : 'hidden'}`}
                aria-label="Faire défiler les images vers la droite"
              >
                <FaChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Description du projet */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
        <h2 className="text-2xl font-bold text-socabeg-blue mb-4">
          {t('realEstateDetail.projectDescription')}
        </h2>
        <p className="text-gray-700 leading-relaxed">
          {project.description}
        </p>
      </div>



      {/* Le + du programme */}
      {project.projectKey !== 'tivaouane' && project.projectKey !== 'almadies2' && project.projectKey !== 'ndiakhirate' && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h2 className="text-2xl font-bold text-socabeg-blue mb-4">
            {t(`projects.${project.projectKey}.programPlus`) || "Le + du programme"}
          </h2>
          {(() => {
            // Utiliser programFeatures s'il existe, sinon utiliser les caractéristiques générales
            const programFeatures = t(`projects.${project.projectKey}.programFeatures`, { returnObjects: true });
            const hasFeatures = Array.isArray(programFeatures) && programFeatures.length > 0;
            
            if (hasFeatures) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {programFeatures.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              );
            } else if (Array.isArray(project.caracteristiques) && project.caracteristiques.length > 0) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.caracteristiques.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              );
            } else {
              return <p className="text-gray-500">{t('realEstateDetail.noAmenities')}</p>;
            }
          })()}
        </div>
      )}

      {/* Overlay plein écran pour l'image */}
      {fullscreenImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4" onClick={closeFullscreenImage}>
          <div className="relative w-full h-full max-w-6xl max-h-[90vh] flex items-center" onClick={e => e.stopPropagation()}>
            {/* Bouton précédent */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                navigateImage(-1);
              }}
              className="absolute left-4 bg-black bg-opacity-50 text-white p-3 rounded-full z-10 hover:bg-opacity-70 transition-all"
              aria-label="Image précédente"
            >
              <FaChevronLeft size={24} />
            </button>
            
            {/* Image en plein écran */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img 
                src={fullscreenImage} 
                alt={`${project.title} - Vue agrandie`} 
                className="max-w-full max-h-full object-contain"
              />
              
              {/* Bouton de fermeture */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  closeFullscreenImage();
                }}
                className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                aria-label="Fermer"
              >
                <FaTimes size={20} />
              </button>
              
              {/* Indicateur de position */}
              {gallery.length > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {gallery.length}
                </div>
              )}
            </div>
            
            {/* Bouton suivant */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                navigateImage(1);
              }}
              className="absolute right-4 bg-black bg-opacity-50 text-white p-3 rounded-full z-10 hover:bg-opacity-70 transition-all"
              aria-label="Image suivante"
            >
              <FaChevronRight size={24} />
            </button>
          </div>
        </div>
      )}

      {/* Modal Vidéo */}
      {project.videoId && showVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4" onClick={() => setShowVideo(false)}>
          <div className="relative w-full max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setShowVideo(false)}
              className="absolute -top-10 right-0 text-white text-2xl hover:text-socabeg-blue transition-colors"
              aria-label="Fermer la vidéo"
            >
              <FaTimes />
            </button>
            <div className="aspect-w-16 aspect-h-9 w-full">
              <iframe
                src={`https://www.youtube.com/embed/${project.videoId}?autoplay=1`}
                title={`Vidéo de présentation - ${project.title}`}
                className="w-full h-[70vh] max-h-[calc(100vh-8rem)] rounded-lg"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectMain;
